import { Loader2, Sparkles } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";

interface Props {
  /// Disabled because the notes are too short (shows the tooltip), or
  /// because a generation/submit is in flight.
  disabled: boolean;
  generating: boolean;
  /// Tooltip shown while disabled and not generating.
  disabledReason?: string;
  onClick: () => void;
}

/// The compose-form AI affordance: small button in the brand gradient
/// (`pulse` variant, same treatment as People's "Add users") with the
/// Sparkles marker the app already uses for AI surfaces.
export function WritingAssistButton({
  disabled,
  generating,
  disabledReason,
  onClick,
}: Props): ReactNode {
  const button = (
    <Button
      type="button"
      variant="pulse"
      size="xs"
      disabled={disabled || generating}
      onClick={onClick}
    >
      {generating ? (
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
      ) : (
        <Sparkles className="size-3.5" aria-hidden />
      )}
      {generating ? "Drafting…" : "Help me write"}
    </Button>
  );

  if (disabled && !generating && disabledReason) {
    return (
      <Tooltip>
        {/* Disabled buttons swallow pointer events; the span stays
            focusable (tabIndex=0) so keyboard users can surface the
            tooltip on a disabled button. */}
        <TooltipTrigger asChild>
          {/* biome-ignore lint/a11y/noNoninteractiveTabindex: reason: wrapping a disabled button so keyboard users can focus to surface the tooltip */}
          <span tabIndex={0}>{button}</span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    );
  }
  return button;
}
